import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  getInvoices,
  getInvoice,
  updateInvoiceStatusAction,
  bulkUpdateInvoiceStatusAction,
  clearCurrentInvoice,
  invoiceLoad,
} from "./invoices.slice";
import { uuid } from "@/interfaces";
import type { ChangedInvoiceStatus, InvoiceListFilters } from "@/repositories/invoice-records";
import { toast } from "sonner";

export function useInvoices(
  page: number = 1,
  pageSize: number = 10,
  filters?: InvoiceListFilters,
  sortKey?: string,
  sortOrder: "asc" | "desc" = "asc"
) {
  const dispatch = useAppDispatch();
  const { list, loading, total, error } = useAppSelector((state) => state.invoices);

  useEffect(() => {
    dispatch(getInvoices({ page, pageSize, filters, sortKey, sortOrder }));
  }, [dispatch, page, pageSize, JSON.stringify(filters), sortKey, sortOrder]);

  return {
    invoices: list,
    loading,
    total,
    error,
  };
}

export function useInvoice(id: uuid | null) {
  const dispatch = useAppDispatch();
  const invoices = useAppSelector((state) => state.invoices);

  useEffect(() => {
    if (id) {
      dispatch(getInvoice(id));
    } else {
      dispatch(clearCurrentInvoice());
    }
  }, [dispatch, id]);

  return invoiceLoad(invoices, id);
}

export function useUpdateInvoiceStatus() {
  const dispatch = useAppDispatch();

  const updateStatus = async (id: uuid, status: ChangedInvoiceStatus) => {
    try {
      const result = await dispatch(updateInvoiceStatusAction({ id, status })).unwrap();
      toast.success("Success", {
        description: "Invoice status updated successfully",
      });
      return result;
    } catch (error: any) {
      toast.error("Error", {
        description: error?.message || "Failed to update invoice status",
      });
      throw error;
    }
  };

  return { updateStatus };
}

export function useBulkUpdateInvoiceStatus() {
  const dispatch = useAppDispatch();

  const bulkUpdateStatus = async (ids: uuid[], status: ChangedInvoiceStatus) => {
    try {
      await dispatch(bulkUpdateInvoiceStatusAction({ ids, status })).unwrap();
      toast.success("Success", {
        description: `${ids.length} invoice(s) status updated successfully`,
      });
    } catch (error: any) {
      toast.error("Error", {
        description: error?.message || "Failed to update invoice statuses",
      });
      throw error;
    }
  };

  return { bulkUpdateStatus };
}

